import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, Navigate } from "react-router-dom";
import { dict } from "../i18n";
import { fetchContent } from "../lib/api";
import SEO from "../components/SEO";
import NotFound from "./NotFound";
import {
  getSectionBySlug,
  getSectionByShowParam,
  pricingSectionUrl,
} from "../lib/pricingSections";

const fmtPrice = (p, lang) => (typeof p === "string" ? p : p?.[lang] || "");

export default function Pricing({ lang }) {
  const t = dict[lang];
  const [clinic, setClinic] = useState(null);
  const { hash, search } = useLocation();
  const { section: sectionSlug } = useParams();

  useEffect(() => {
    fetchContent().then(setClinic).catch(() => {});
  }, []);

  // ── URL parsing ────────────────────────────────────────────────
  // Preferred (new): /hinnakiri/<slug>   → path param
  // Legacy support: /hinnakiri?show=id1,id2 → query param
  //
  // If someone hits the legacy URL, redirect to the new one so Google
  // consolidates rankings on a single canonical.
  const showParam = useMemo(() => {
    const p = new URLSearchParams(search);
    return p.get("show");
  }, [search]);

  const sectionFromSlug = sectionSlug ? getSectionBySlug(sectionSlug) : null;
  const sectionFromShow = !sectionSlug && showParam ? getSectionByShowParam(showParam) : null;

  const section = sectionFromSlug;
  const showFilter = section ? section.groups : null;

  const filteredGroups = useMemo(() => {
    if (!clinic) return [];
    if (!showFilter) return clinic.pricing || [];
    const map = Object.fromEntries((clinic.pricing || []).map((g) => [g.id, g]));
    return showFilter.map((id) => map[id]).filter(Boolean);
  }, [clinic, showFilter]);

  const matchedService = useMemo(() => {
    if (!clinic || !section) return null;
    return (clinic.services || []).find((s) => s.id === section.serviceId);
  }, [clinic, section]);

  // Scroll to anchor when hash present and content is loaded
  useEffect(() => {
    if (clinic && hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
  }, [clinic, hash]);

  // Redirects — placed AFTER all hooks are called (rules-of-hooks).
  //
  // 1. Legacy /hinnakiri?show=X,Y  →  /hinnakiri/<slug>  (canonical consolidation)
  if (sectionFromShow) {
    return <Navigate to={pricingSectionUrl(sectionFromShow.slugs.et, lang)} replace />;
  }
  // 2. Unknown slug → render NotFound with noindex meta so Google drops
  //    the URL instead of consolidating rank on the /hinnakiri listing.
  if (sectionSlug && !sectionFromSlug) {
    return <NotFound lang={lang} />;
  }
  // 3. Section slug is from a DIFFERENT language than the current URL prefix
  //    (e.g. /ru/цены/kilpnaarme-uuringud — Estonian slug in RU URL).
  //    Redirect to the properly localised canonical.
  if (section && sectionSlug !== section.slugs[lang]) {
    return <Navigate to={pricingSectionUrl(section.slugs.et, lang)} replace />;
  }

  const pageHeading = section
    ? section.title[lang]
    : t.sections.pricing.title;

  return (
    <>
      <SEO
        lang={lang}
        page="pricing"
        pathOverride={section ? `/hinnakiri/${section.slugs.et}` : undefined}
        title={
          section
            ? `${section.title[lang]} — ${
                lang === "ru" ? "Цены" : lang === "en" ? "Pricing" : "Hinnakiri"
              } — Tallinna Endokrinoloogia Kliinik`
            : undefined
        }
        description={
          section
            ? (lang === "et"
                ? `${section.title[lang]} — hinnakiri Tallinna Endokrinoloogia Kliinikus. Hinnad kehtivad Tallinnas ja Pärnus. Kindlustusega ja saatekirjaga tavaliselt tasuta.`
                : lang === "ru"
                ? `${section.title[lang]} — прайс-лист клиники эндокринологии в Таллинне. Цены действуют в Таллинне и Пярну. Со страховкой обычно бесплатно.`
                : `${section.title[lang]} — pricing at Tallinn Endocrinology Clinic. Applies in Tallinn and Pärnu. Usually free with health insurance and referral.`)
            : undefined
        }
        offers={{ name: pageHeading, groups: filteredGroups }}
        breadcrumb={
          section
            ? [
                { name: t.sections.pricing.title, path: pricingSectionUrl(null, lang) },
                { name: section.title[lang], path: pricingSectionUrl(section.slugs.et, lang) },
              ]
            : undefined
        }
      />
      <section className="container-page pt-12 pb-8" data-testid="pricing-page">
        <div className="chip mb-3">{t.sections.pricing.tag}</div>
        <h1 className="section-title" data-testid="pricing-page-title">
          {pageHeading}
        </h1>
        <p className="text-surface-inkSoft mt-3 text-[15px] max-w-3xl">
          {matchedService
            ? matchedService.summary[lang]
            : lang === "et"
            ? "Alljärgnev hinnakiri kehtib nii Tallinnas kui Pärnus. Tervisekassa kindlustusega ja saatekirjaga vastuvõtt on enamasti tasuta või soodushinnaga."
            : lang === "ru"
            ? "Прайс-лист действует в Таллинне и Пярну. С медстраховкой и направлением визит обычно бесплатный или со скидкой."
            : "The price list applies in Tallinn and Pärnu. With insurance and referral the visit is usually free or discounted."}
        </p>
        {section && (
          <a
            href={pricingSectionUrl(null, lang)}
            data-testid="pricing-show-all-link"
            className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-medium text-sapphire-700 hover:underline"
          >
            ← {lang === "et" ? "Vaata kogu hinnakirja" : lang === "ru" ? "Смотреть весь прайс" : "View full price list"}
          </a>
        )}
      </section>

      <section className="container-page pb-20">
        <div className="space-y-5">
          {filteredGroups.map((group, gi) => (
            <div
              key={group.id || gi}
              id={group.id}
              className="card-clinic !p-0 overflow-hidden scroll-mt-24"
              data-testid={`pricing-group-${group.id || gi}`}
            >
              <div className="px-6 py-4 bg-sapphire-50/60 border-b border-surface-outline/40">
                <h2 className="text-[17px] font-semibold text-sapphire-700">{group.group[lang]}</h2>
              </div>
              <div className="divide-y divide-surface-outline/40">
                {group.items.map((item, ii) => (
                  <div key={ii} className="flex items-center justify-between gap-6 px-6 py-4" data-testid={`pricing-item-${group.id}-${ii}`}>
                    <div className="text-[14px] text-surface-ink leading-relaxed">{item.name[lang]}</div>
                    <div className="text-[15px] font-semibold text-surface-ink shrink-0 font-mono tabular-nums">
                      {fmtPrice(item.price, lang)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl bg-amber-50/60 border border-amber-200/60 p-5">
          <div className="text-[13px] font-semibold uppercase tracking-wider text-amber-700 mb-1">
            {lang === "et" ? "Tühistamise reegel" : lang === "ru" ? "Правило отмены" : "Cancellation policy"}
          </div>
          <p className="text-[14px] text-amber-900/90 leading-relaxed">
            {clinic?.cancellation?.[lang]}
          </p>
        </div>
      </section>
    </>
  );
}
